import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import Header from "../../components/feature/Header";
import Footer from "../../components/feature/Footer";

export default function TPLSPPage() {
  const { t } = useTranslation();

  return (
    <>
      <Helmet>
        <title>{t("tplsp.seo.title")}</title>
        <meta name="description" content={t("tplsp.seo.description")} />
        <meta name="keywords" content={t("tplsp.seo.keywords")} />
        <meta property="og:title" content={t("tplsp.seo.title")} />
        <meta property="og:description" content={t("tplsp.seo.description")} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="/tplsp_banner_2000x716.png" />
        <link rel="canonical" href="https://autochain.com.hk/tplsp" />
        
        {/* Structured Data - Government Service */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "GovernmentService",
            "name": "第三方物流服務供應商資助先導計劃 (TPLSP)",
            "description": "香港政府資助計劃，資助第三方物流服務供應商採用科技，提升生產力及服務水平。最高資助 HK$2,000,000。",
            "serviceType": "政府資助計劃",
            "provider": {
              "@type": "GovernmentOrganization",
              "name": "香港生產力促進局 (HKPC)"
            },
            "areaServed": {
              "@type": "Place",
              "name": "香港"
            },
            "url": "https://autochain.com.hk/tplsp"
          })}
        </script>
        
        {/* Structured Data - Breadcrumb */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "首頁", "item": "https://autochain.com.hk/" },
              { "@type": "ListItem", "position": 2, "name": "第三方物流資助計劃", "item": "https://autochain.com.hk/tplsp" }
            ]
          })}
        </script>
      </Helmet>

      <Header />
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-orange-50 to-orange-100 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="inline-flex items-center space-x-2 bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
                <i className="ri-government-line"></i>
                <span>香港政府資助計劃</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                {t("tplsp.title")}
              </h1>
              <p className="text-xl text-gray-700 mb-4">
                {t("tplsp.subtitle")}
              </p>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                {t("tplsp.description")}
              </p>
            </div>
          </div>
        </section>

        {/* Updates Section */}
        <section className="py-16 bg-orange-500">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
                    <i className="ri-notification-line text-white text-2xl"></i>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {t("tplsp.updates.title")}
                  </h3>
                  <div className="space-y-3 text-gray-700">
                    <div className="flex items-start space-x-3">
                      <i className="ri-checkbox-circle-fill text-orange-500 text-xl mt-1"></i>
                      <p>
                        <strong>提高資助上限：</strong>
                        每間申請企業的累計資助額上限由一百萬元增至
                        <strong className="text-orange-600">二百萬元</strong>
                      </p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <i className="ri-checkbox-circle-fill text-orange-500 text-xl mt-1"></i>
                      <p>
                        <strong>擴大資助範圍：</strong>
                        {t("tplsp.updates.scope_expansion")}
                      </p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <i className="ri-checkbox-circle-fill text-orange-500 text-xl mt-1"></i>
                      <p>
                        <strong>新增培訓資助：</strong>
                        {t("tplsp.updates.training_support")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Overview Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {t("tplsp.overview.title")}
              </h2>
              <div className="w-20 h-1 bg-orange-500 mx-auto"></div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-gradient-to-br from-orange-50 to-white p-6 rounded-xl border-2 border-orange-200 hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-orange-500 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-time-line text-white text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {t("tplsp.overview.duration")}
                </h3>
                <p className="text-3xl font-bold text-orange-600 mb-1">0-24</p>
                <p className="text-gray-600">個月</p>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-white p-6 rounded-xl border-2 border-orange-200 hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-orange-500 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-percent-line text-white text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {t("tplsp.overview.subsidy_ratio")}
                </h3>
                <p className="text-3xl font-bold text-orange-600 mb-1">50%</p>
                <p className="text-gray-600">項目總核準開支</p>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-white p-6 rounded-xl border-2 border-orange-200 hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-orange-500 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-money-dollar-circle-line text-white text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {t("tplsp.overview.max_amount")}
                </h3>
                <p className="text-2xl font-bold text-orange-600 mb-1">
                  HK$2,000,000
                </p>
                <p className="text-gray-600">最高上限</p>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-white p-6 rounded-xl border-2 border-orange-200 hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-orange-500 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-file-list-3-line text-white text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {t("tplsp.overview.max_projects")}
                </h3>
                <p className="text-3xl font-bold text-orange-600 mb-1">4個</p>
                <p className="text-gray-600">最多可獲資助</p>
              </div>
            </div>
          </div>
        </section>

        {/* Eligibility Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {t("tplsp.eligibility.title")}
              </h2>
              <div className="w-20 h-1 bg-orange-500 mx-auto"></div>
            </div>
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
              <p className="text-lg text-gray-700 mb-8">
                {t("tplsp.eligibility.description")}
              </p>
              <div className="space-y-6">
                <div className="flex items-start space-x-4 p-6 bg-orange-50 rounded-xl">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold">
                      1
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-800 text-lg">
                      在提供<strong>第三方物流服務</strong>，如供應鏈及分銷管理等增值服務
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4 p-6 bg-orange-50 rounded-xl">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold">
                      2
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-800 text-lg">
                      業務須與<strong>進口或出口物流服務</strong>有關
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4 p-6 bg-orange-50 rounded-xl">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold">
                      3
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-800 text-lg">
                      在項目進行期間必須<strong>維持符合申請資格的條件</strong>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Funding Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {t("tplsp.funding.title")}
              </h2>
              <div className="w-20 h-1 bg-orange-500 mx-auto"></div>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-orange-50 to-white p-8 rounded-2xl border-2 border-orange-200 shadow-lg">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center">
                    <i className="ri-funds-line text-white text-3xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {t("tplsp.funding.first_payment")}
                  </h3>
                </div>
                <div className="space-y-4">
                  <p className="text-gray-700 text-lg">
                    成功申請的企業可於<strong>簽訂資助協議後</strong>獲取核準撥款總數的
                  </p>
                  <div className="text-center py-4">
                    <span className="text-5xl font-bold text-orange-600">50%</span>
                    <p className="text-gray-600 mt-2">作為項目首期撥款</p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-white p-8 rounded-2xl border-2 border-orange-200 shadow-lg">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center">
                    <i className="ri-checkbox-circle-line text-white text-3xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {t("tplsp.funding.final_payment")}
                  </h3>
                </div>
                <div className="space-y-4">
                  <p className="text-gray-700 text-lg">
                    {t("tplsp.funding.final_payment_desc")}
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start space-x-3">
                      <i className="ri-check-line text-orange-500 text-xl mt-1"></i>
                      <span className="text-gray-700">項目完成</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <i className="ri-check-line text-orange-500 text-xl mt-1"></i>
                      <span className="text-gray-700">
                        終期報告獲管理委員會及秘書處接納
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Scope Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {t("tplsp.scope.title")}
              </h2>
              <div className="w-20 h-1 bg-orange-500 mx-auto mb-6"></div>
              <p className="text-xl text-gray-700 max-w-3xl mx-auto">
                {t("tplsp.scope.description")}
              </p>
            </div>
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-8 flex items-center">
                <i className="ri-lightbulb-line text-orange-500 text-3xl mr-3"></i>
                {t("tplsp.scope.subtitle")}
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-center space-x-3 p-4 bg-orange-50 rounded-lg hover:shadow-md transition-shadow">
                  <i className="ri-robot-line text-orange-500 text-2xl"></i>
                  <div>
                    <h4 className="font-bold text-gray-900">
                      {t("tplsp.scope.automation")}
                    </h4>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-4 bg-orange-50 rounded-lg hover:shadow-md transition-shadow">
                  <i className="ri-exchange-line text-orange-500 text-2xl"></i>
                  <div>
                    <h4 className="font-bold text-gray-900">
                      {t("tplsp.scope.edi")}
                    </h4>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-4 bg-orange-50 rounded-lg hover:shadow-md transition-shadow">
                  <i className="ri-truck-line text-orange-500 text-2xl mt-1"></i>
                  <div>
                    <h4 className="font-bold text-gray-900">
                      {t("tplsp.scope.tms")}
                    </h4>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-4 bg-orange-50 rounded-lg hover:shadow-md transition-shadow">
                  <i className="ri-building-line text-orange-500 text-2xl mt-1"></i>
                  <div>
                    <h4 className="font-bold text-gray-900">
                      {t("tplsp.scope.erp")}
                    </h4>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-4 bg-orange-50 rounded-lg hover:shadow-md transition-shadow">
                  <i className="ri-line-chart-line text-orange-500 text-2xl mt-1"></i>
                  <div>
                    <h4 className="font-bold text-gray-900">
                      {t("tplsp.scope.analytics")}
                    </h4>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-4 bg-orange-50 rounded-lg hover:shadow-md transition-shadow">
                  <i className="ri-global-line text-orange-500 text-2xl mt-1"></i>
                  <div>
                    <h4 className="font-bold text-gray-900">
                      {t("tplsp.scope.infrastructure")}
                    </h4>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-4 bg-orange-50 rounded-lg hover:shadow-md transition-shadow">
                  <i className="ri-store-3-line text-orange-500 text-2xl mt-1"></i>
                  <div>
                    <h4 className="font-bold text-gray-900">
                      {t("tplsp.scope.wms")}
                    </h4>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-4 bg-orange-50 rounded-lg hover:shadow-md transition-shadow">
                  <i className="ri-map-pin-line text-orange-500 text-2xl mt-1"></i>
                  <div>
                    <h4 className="font-bold text-gray-900">
                      {t("tplsp.scope.rtls")}
                    </h4>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-4 bg-orange-50 rounded-lg hover:shadow-md transition-shadow">
                  <i className="ri-wifi-line text-orange-500 text-2xl mt-1"></i>
                  <div>
                    <h4 className="font-bold text-gray-900">
                      {t("tplsp.scope.iot")}
                    </h4>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-4 bg-orange-50 rounded-lg hover:shadow-md transition-shadow">
                  <i className="ri-scan-line text-orange-500 text-2xl mt-1"></i>
                  <div>
                    <h4 className="font-bold text-gray-900">
                      {t("tplsp.scope.security")}
                    </h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {t("tplsp.process.title")}
              </h2>
              <div className="w-20 h-1 bg-orange-500 mx-auto"></div>
            </div>
            <div className="relative">
              <div className="hidden md:block absolute left-0 right-0 h-1 bg-orange-200" style={{ top: 'calc(50% + 12px)' }}></div>
              <div className="grid md:grid-cols-4 gap-8 relative">
                <div className="text-center">
                  <div className="w-20 h-20 bg-orange-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6 relative z-10 shadow-lg">
                    1
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {t("tplsp.process.step1")}
                  </h3>
                  <p className="text-gray-600">{t("tplsp.process.step1_desc")}</p>
                </div>
                <div className="text-center">
                  <div className="w-20 h-20 bg-orange-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6 relative z-10 shadow-lg">
                    2
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {t("tplsp.process.step2")}
                  </h3>
                  <p className="text-gray-600">{t("tplsp.process.step2_desc")}</p>
                </div>
                <div className="text-center">
                  <div className="w-20 h-20 bg-orange-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6 relative z-10 shadow-lg">
                    3
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {t("tplsp.process.step3")}
                  </h3>
                  <p className="text-gray-600">{t("tplsp.process.step3_desc")}</p>
                </div>
                <div className="text-center">
                  <div className="w-20 h-20 bg-orange-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6 relative z-10 shadow-lg">
                    4
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {t("tplsp.process.step4")}
                  </h3>
                  <p className="text-gray-600">{t("tplsp.process.step4_desc")}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* DIY Section */}
        <section className="py-16 bg-gradient-to-br from-orange-500 to-orange-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {t("tplsp.diy.title")}
              </h2>
              <div className="w-20 h-1 bg-white mx-auto mb-6"></div>
              <p className="text-xl text-orange-50 max-w-3xl mx-auto">
                {t("tplsp.diy.subtitle")}
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20 hover:bg-white/20 transition-all">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-6">
                  <i className="ri-award-line text-orange-500 text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">
                  {t("tplsp.diy.step1.title")}
                </h3>
                <p className="text-orange-50 text-lg mb-4">
                  1）先透過HKPC申請資格檢查確定申請資格<br />
                  2）透過申請系統建立帳號
                </p>
                <a
                  href="https://bee.hkpc.org/tc/funding-schemes/tplsp/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-white hover:text-orange-100 underline transition-colors cursor-pointer"
                >
                  <i className="ri-external-link-line mr-2"></i>
                  前往HKPC申請系統
                </a>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20 hover:bg-white/20 transition-all">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-6">
                  <i className="ri-team-line text-orange-500 text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">
                  {t("tplsp.diy.step2.title")}
                </h3>
                <p className="text-orange-50 text-lg">
                  {t("tplsp.diy.step2.desc")}
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20 hover:bg-white/20 transition-all">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-6">
                  <i className="ri-speed-up-line text-orange-500 text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">
                  {t("tplsp.diy.step3.title")}
                </h3>
                <p className="text-orange-50 text-lg">
                  {t("tplsp.diy.step3.desc")}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-3xl p-12 shadow-xl">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                {t("tplsp.cta.title")}
              </h2>
              <p className="text-xl text-gray-700 mb-8">
                {t("tplsp.cta.description")}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
                <button
                  onClick={() => {
                    const message = encodeURIComponent(
                      "您好！我想了解第三方物流資助計劃的申請流程，希望獲取項目計劃書template及RFQ template。"
                    );
                    window.open(
                      `https://wa.me/85294252903?text=${message}`,
                      "_blank"
                    );
                  }}
                  className="inline-flex items-center justify-center px-8 py-4 bg-orange-500 text-white text-lg font-bold rounded-full hover:bg-orange-600 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line text-2xl mr-2"></i>
                  {t("tplsp.cta.whatsapp")}
                </button>
                <a
                  href="tel:+85294252903"
                  className="inline-flex items-center justify-center px-8 py-4 bg-gray-900 text-white text-lg font-bold rounded-full hover:bg-gray-800 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line text-2xl mr-2"></i>
                  {t("tplsp.cta.phone")}
                </a>
              </div>
              <div className="space-y-2 text-gray-700">
                <p className="flex items-center justify-center space-x-2">
                  <i className="ri-phone-line text-orange-500"></i>
                  <span>電話：{t("tplsp.cta.phone_number")}</span>
                </p>
                <p className="flex items-center justify-center space-x-2">
                  <i className="ri-mail-line text-orange-500"></i>
                  <span>電郵：{t("tplsp.cta.email")}</span>
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
}